import React, { useContext } from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import moment from "moment";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { STORAGE_BUCKET, IMG_PLACEHOLDER, bakeStreetSquareLogo } from "../../common/constants";
import { Link as RouterLink } from "react-router-dom";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { getCustomerName, hasManagerAccess, getOptimizedCloudinaryUrl } from "../../common/util";
import OrderProgress from "../../order/component/OrderProgress";
import OrderPrint from "../screen/OrderPrint";
import { UserContext } from "../../context/UserContext";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import currency from "currency.js";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import WatchLaterIcon from "@mui/icons-material/WatchLater";

function getFulfillmentTypeIcon(type) {
  if (type === "delivery") return LocalShippingOutlinedIcon;
  if (type === "pickup") return LocalMallOutlinedIcon;
  return ShoppingBagOutlinedIcon;
}

function getFulfillmentDetail(fulfillment) {
  if (fulfillment.type === "delivery") {
    return fulfillment.provider ? fulfillment.provider.name : "In-House";
  }
  if (fulfillment.type === "pickup") {
    return (
      fulfillment.location?.street || fulfillment.address?.split(",")[0] || null
    );
  }
  return null;
}

function FulfillmentStatusIcon({ status }) {
  const iconStyle = { fontSize: "1.2rem" };
  if (status === "fulfilled" || status === "completed") {
    return <CheckCircle sx={{ ...iconStyle, color: "success.main" }} />;
  }
  if (status === "canceled") {
    return <Cancel sx={{ ...iconStyle, color: "error.main" }} />;
  }
  return <WatchLaterIcon sx={{ ...iconStyle, color: "#888" }} />;
}

const metaRowSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  columnGap: "5px",
  rowGap: "2px",
  fontSize: ".8rem",
  textTransform: "uppercase",
  minWidth: 0,
};

function MetaSegment({ children, sx }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        whiteSpace: "nowrap",
        flex: "0 0 auto",
        maxWidth: "100%",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function MetaBullet() {
  return (
    <Box component="span" sx={{ color: "#CCC", flexShrink: 0 }}>
      ●
    </Box>
  );
}

export default function BakerOrderSummary(props) {
  const { order, readOnly } = props;
  const { user } = useContext(UserContext);

  const printed = order?.activity?.print;
  const lastPrintTs = printed && printed[printed.length - 1].ts;

  let image = `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`;
  if (order.items && order.items.length > 0) {
    for (const item of order.items) {
      if (item.images && item.images.length > 0) {
        image = item.images[0];
        break;
      }
    }
  } else if (order.details && order.details.pics) {
    image = order.details.pics[0];
  }

  let isManager = hasManagerAccess(user.role);
  const fulfillmentType = order.fulfillment?.type;
  const FulfillmentTypeIcon = fulfillmentType ? getFulfillmentTypeIcon(fulfillmentType) : null;
  const fulfillmentDetail = fulfillmentType ? getFulfillmentDetail(order.fulfillment) : null;
  const hasPaymentDue = +order.payment.due > 0;
  const paymentDueDate = order.payment.dueDate
    ? moment(order.payment.dueDate)
    : moment(order.fulfillment.date).subtract(7, "days");
  const isPastDue = hasPaymentDue && paymentDueDate.isBefore(moment(), "day");

  const paymentStatus =
    order.status === "canceled" ? null : hasPaymentDue ? (
      <Box component="span" sx={isPastDue ? { color: "error.main" } : undefined}>
        {isPastDue
          ? ` (${currency(order.payment.due).format()} PAST-DUE)`
          : ` (${currency(order.payment.due).format()} due)`}
      </Box>
    ) : (
      " (Paid in-full)"
    );

  return (
    <Box mb={4}>
      <Paper elevation={0}>
        <Box px={2} pt={2} pb={1}>
          <Grid container spacing={2}>
            <Grid item sm={3} xs={4}>
              <img
                src={getOptimizedCloudinaryUrl(image, "f_auto,q_auto,w_400")}
                style={{ width: "100%", aspectRatio: "1", objectFit: "cover" }}
                alt=""
              />
            </Grid>
            <Grid item sm={9} xs={8} sx={{ minWidth: 0 }}>
              <Typography variant="h6" gutterBottom sx={{ textTransform: "capitalize" }}>
                {getCustomerName(order.customer) || "Walk-In"}{" "}
              </Typography>
              <Box sx={{ color: "#888", minWidth: 0 }}>
                <Typography variant="overline" sx={metaRowSx}>
                  <MetaSegment>
                    <EventOutlinedIcon style={{ fontSize: "1.2rem" }} />
                    {moment(order.fulfillment.date).format("ddd, MMM D")}
                  </MetaSegment>
                  <MetaSegment>
                    <MetaBullet />
                    {order.fulfillment.time}
                  </MetaSegment>
                </Typography>
                {fulfillmentType && FulfillmentTypeIcon && (
                  <Typography variant="overline" sx={metaRowSx}>
                    <MetaSegment>
                      <FulfillmentTypeIcon style={{ fontSize: "1.2rem" }} />
                      {fulfillmentType}
                    </MetaSegment>
                    {fulfillmentDetail && (
                      <MetaSegment
                        sx={{
                          flex: "0 1 auto",
                          minWidth: 0,
                          overflow: "hidden",
                        }}
                      >
                        <MetaBullet />
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                          }}
                        >
                          {fulfillmentDetail}
                        </Box>
                      </MetaSegment>
                    )}
                    <MetaSegment sx={{ flexShrink: 0 }}>
                      <FulfillmentStatusIcon status={order.status} />
                    </MetaSegment>
                  </Typography>
                )}
                <Typography variant="overline" sx={metaRowSx}>
                  <MetaSegment>
                    <ReceiptLongOutlinedIcon style={{ fontSize: "1.2rem" }} />
                    {`${currency(order.src === "mkt" ? order.payment.bakerTotal : order.payment.total).format()}`}
                    {paymentStatus}
                  </MetaSegment>
                  {isManager && (
                    <MetaSegment>
                      <MetaBullet />
                      {order.items?.length} item(s)
                    </MetaSegment>
                  )}
                </Typography>
                {props.showTs && (
                  <Typography variant="overline" sx={metaRowSx}>
                    <MetaSegment>
                      <AccessTimeOutlinedIcon style={{ fontSize: "1.2rem" }} />
                      Ordered {moment(order.ts._seconds * 1000).fromNow()}
                    </MetaSegment>
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
        {!readOnly && (
          <Box px={2} pb={1}>
            <Grid container alignItems="center">
              <Grid item sm={3} xs={4}>
                <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
                  {isManager && order.status === "confirmed" && (
                    <OrderProgress order={order} />
                  )}
                  {isManager && printed && lastPrintTs && <OrderPrint order={order} />}
                </Box>
              </Grid>
              <Grid item sm={9} xs={8}>
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {isManager && order.autoQuote && (
                    <AutoAwesomeOutlinedIcon sx={{ color: "#999" }} />
                  )}
                  {order.src === "mkt" && (
                    <img
                      src={bakeStreetSquareLogo}
                      style={{ width: "32px", height: "32px" }}
                      alt=""
                    />
                  )}
                  <Button
                    color={order.status === "pending_review" ? "warning" : "primary"}
                    disableElevation
                    variant="contained"
                    to={`/order/${order.id}`}
                    component={RouterLink}
                  >
                    {order.status === "pending_review" ? "CONFIRM ORDER" : "VIEW ORDER"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
